<template>
  <div ref="reviewCard" class="border-b border-gray-200 pb-6 last:border-0" :class="{ 'animate-highlight': animateHighlight }">
    <div class="flex items-start gap-4 relative">
      <UAvatar :src="review.avatar" :alt="review.name" size="md" />
      <div class="flex-1 space-y-4">
        <div class="flex items-center gap-2 mb-2">
          <h4 class="font-semibold text-gray-900">{{ review.name }}</h4>
          <div class="flex items-center">
            <Icon name="i-heroicons-star-solid" class="w-4 h-4 text-yellow-400" />
            <span class="text-sm text-gray-600 ml-1">{{ review.rating }}</span>
          </div>
          <span class="text-sm text-gray-500">{{ moment(review.createdAt).format('LL') }}</span>
        </div>
        <p class="text-sm text-gray-600">{{ review.comment }}</p>

        <div class="flex gap-2 items-center">
          <ProductReviewModal v-if="review.owns" :productId="review.productId" :reviewId="review.id" @review:update="handleReviewUpdate" >
            <UButton  variant="ghost" color="neutral" size="sm" icon="i-heroicons-pencil"  />
          </ProductReviewModal>
          <ConfirmDialog v-if="review.owns" @confirm="handleDelete">
            <UButton  variant="ghost" color="error" size="sm" icon="i-heroicons-trash" />
          </ConfirmDialog>
        </div>
      </div>
      <div class="flex flex-col gap-2 items-center">
        <ProductReviewLikesButton :reviewId="review.id" :likes="review.likes" :liked="review.likedByUser" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductReview } from '~/types'
import ProductReviewLikesButton from '~/components/atoms/ProductReviewLikesButton.vue';
import ProductReviewModal from '../organisms/ProductReviewModal.vue';
import ConfirmDialog from './ConfirmDialog.vue';
import moment from 'moment';

const {
  highlight = false,
  ...props
} = defineProps<{
  review: ProductReview
  highlight?: boolean
}>()

const emit = defineEmits<{
  (e: 'review:deleted', reviewId: number): void;
}>();

const review = ref<ProductReview>(props.review);

const reviewCard = ref<HTMLElement | null>(null);

const animateHighlight = ref(highlight);

watchEffect(() => {
  if (highlight) {
    animateHighlight.value = true;
  }
});

onMounted(() => {
    setTimeout(() => {
      if (highlight && reviewCard.value) {
        const top = reviewCard.value.getBoundingClientRect().top + window.scrollY - 200;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
      }
    }, 100);
  }
);

const handleReviewUpdate = (updatedReview: ProductReview) => {
  review.value = { ...updatedReview };
  animateHighlight.value = true;
};

const catalogService = useCatalogService();


const pendingDelete = ref(false);

const toast = useToast();

const handleDelete = async () => {
  pendingDelete.value = true;

  try {
    await catalogService.removeReview(review.value.id);
    toast.add({
      title: 'Review Deleted',
      description: 'Your review has been successfully deleted.',
      color: 'info'
    });

    emit('review:deleted', review.value.id);
  } catch (error) {
    console.error('Error deleting review:', error);
    toast.add({
      title: 'Error Deleting Review',
      description: 'Failed to delete review',
      color: 'error'
    });
  } finally {
    pendingDelete.value = false;
  }
}
</script>